// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// Sweeping a profile along a three-dimensional path (M3-06).
//
// A ramp is a strip with a wall down each side; a habitrail is a pair of tubes. Both are the
// same operation — carry a cross-section along a curve — and both are arithmetic, so both are
// here, with no Three.js and a `node` test.
//
// **The frame is the whole problem.** Sweeping needs a lateral and an up direction at every
// sample, and the textbook answer is a Frenet frame built from the curve's own second
// derivative. That is wrong for this job twice over: it is undefined wherever the curve is
// briefly straight — which is most of a pinball ramp — and it *rolls* around an inflection,
// so a habitrail's two wires swap sides halfway along and the ball appears to ride through
// them. What a ramp actually has is a fixed idea of up: the playfield's `+z`. So the lateral
// direction is `ẑ × t̂` and the up direction is `t̂ × lateral`, which is stable everywhere a
// ramp can go and degenerates only if a ramp ever climbs vertically, which no ramp does.
//
// Everything is in the playfield frame of PH-001: metres, +x right, +y up the playfield, +z
// out of the surface.

export type Vec3 = readonly [number, number, number]

/** Lateral and up unit vectors for a tangent, anchored to the playfield's own up. */
export function frameFor(tangent: Vec3): { lateral: Vec3; up: Vec3 } {
  const [tx, ty, tz] = tangent
  const tl = Math.hypot(tx, ty, tz)
  if (tl < 1e-12) throw new Error('frameFor: a path sample has no direction')
  const t: Vec3 = [tx / tl, ty / tl, tz / tl]

  // ẑ × t̂ — a purely horizontal lateral, which is what keeps a ramp's floor level across its
  // width however steeply it climbs.
  let lx = -t[1]
  let ly = t[0]
  const ll = Math.hypot(lx, ly)
  if (ll < 1e-9) {
    throw new Error('frameFor: a path climbing vertically has no lateral direction')
  }
  lx /= ll
  ly /= ll
  const lateral: Vec3 = [lx, ly, 0]

  // t̂ × lateral, completing a right-handed frame. It leans with the slope, which is what a
  // ramp's walls do.
  const up: Vec3 = [
    t[1] * 0 - t[2] * ly,
    t[2] * lx - t[0] * 0,
    t[0] * ly - t[1] * lx,
  ]
  return { lateral, up }
}

/** Tangents at every sample of a path, forward-differenced and end-clamped. */
export function tangents(points: readonly Vec3[]): Vec3[] {
  const n = points.length
  if (n < 2) throw new Error('tangents: a path needs at least two points')
  const out: Vec3[] = []
  for (let i = 0; i < n; i++) {
    // Central where there is a point either side, one-sided at the ends. A central difference
    // is what stops a wall kinking at every control point of a Catmull-Rom sampling.
    const a = points[i === 0 ? 0 : i - 1]!
    const b = points[i === n - 1 ? n - 1 : i + 1]!
    out.push([b[0] - a[0], b[1] - a[1], b[2] - a[2]])
  }
  return out
}

/**
 * A path shifted sideways and vertically in its own frame.
 *
 * Both offsets are in the swept frame rather than in playfield coordinates, so a ramp's left
 * wall stays on the left however the ramp turns, and stays the same height above the floor
 * however steeply it climbs.
 */
export function offsetPath(points: readonly Vec3[], lateral: number, vertical: number): Vec3[] {
  const ts = tangents(points)
  return points.map((p, i) => {
    const f = frameFor(ts[i]!)
    return [
      p[0] + f.lateral[0] * lateral + f.up[0] * vertical,
      p[1] + f.lateral[1] * lateral + f.up[1] * vertical,
      p[2] + f.lateral[2] * lateral + f.up[2] * vertical,
    ] as Vec3
  })
}

/**
 * The surface between two paths of equal length.
 *
 * With `a` on the left and `b` on the right of the direction of travel, the surface faces
 * **up**. Pass `twoSided` for a ramp wall, which is genuinely seen from both faces: from
 * outside as part of the machine, and from inside by anyone watching a ball go up it.
 */
export function stripTriangles(
  a: readonly Vec3[],
  b: readonly Vec3[],
  twoSided = false,
): Float32Array {
  if (a.length !== b.length) throw new Error('stripTriangles: edges of different lengths')
  const spans = a.length - 1
  if (spans < 1) throw new Error('stripTriangles: an edge needs at least two points')

  const out = new Float32Array(spans * (twoSided ? 4 : 2) * 9)
  let w = 0
  const push = (p: Vec3): void => {
    out[w++] = p[0]
    out[w++] = p[1]
    out[w++] = p[2]
  }
  for (let i = 0; i < spans; i++) {
    const j = i + 1
    push(a[i]!)
    push(b[i]!)
    push(b[j]!)
    push(a[i]!)
    push(b[j]!)
    push(a[j]!)
    if (!twoSided) continue
    push(a[i]!)
    push(b[j]!)
    push(b[i]!)
    push(a[i]!)
    push(a[j]!)
    push(b[j]!)
  }
  return out
}

/** Continuous reflection normals along one sheet; its reverse face remains separate. */
export function stripNormals(a:readonly Vec3[],b:readonly Vec3[],twoSided=false):Float32Array {
 if(a.length!==b.length||a.length<2)throw new Error('stripNormals: matching paths required')
 const unit=(v:Vec3):Vec3=>{const d=Math.hypot(...v);return d>1e-12?[v[0]/d,v[1]/d,v[2]/d]:[0,0,0]}
 const spans:Vec3[]=a.slice(0,-1).map((p,i)=>{
  const q=b[i]!,r=a[i+1]!,u=[q[0]-p[0],q[1]-p[1],q[2]-p[2]],v=[r[0]-p[0],r[1]-p[1],r[2]-p[2]]
  return unit([u[1]!*v[2]!-u[2]!*v[1]!,u[2]!*v[0]!-u[0]!*v[2]!,u[0]!*v[1]!-u[1]!*v[0]!])
 })
 const normals=a.map((_,i)=>{const p=spans[Math.max(0,i-1)]!,q=spans[Math.min(i,spans.length-1)]!;return unit([p[0]+q[0],p[1]+q[1],p[2]+q[2]])})
 const out:number[]=[]
 for(let i=0;i<spans.length;i++){
  const p=normals[i]!,q=normals[i+1]!
  for(const n of [p,p,q,p,q,q])out.push(...n)
  if(twoSided)for(const n of [p,q,p,p,q,q])out.push(-n[0],-n[1],-n[2])
 }
 return new Float32Array(out)
}

/**
 * A round tube swept along a path — a habitrail wire, or a ramp's top rail.
 *
 * Open at both ends. A wire's ends are inside a bracket or run off the edge of what anyone
 * can see, so capping them costs triangles for nothing.
 */
export function tubeTriangles(points: readonly Vec3[], radius: number, sides = 6): Float32Array {
  const n = points.length
  if (n < 2) throw new Error('tubeTriangles: a path needs at least two points')
  const ts = tangents(points)
  // Circular wires can have vertical posts (the ramp's U-shaped support cradle).
  // Keep floor/profile sweeps strict in frameFor; only a round tube may borrow
  // a neighbouring horizontal orientation, since it has no privileged floor.
  const frames: { lateral: Vec3; up: Vec3 }[] = []
  const horizontal = ts.find(t => Math.hypot(t[0],t[1]) > 1e-9*Math.hypot(...t))
  for(const t of ts) {
    const length=Math.hypot(...t)
    if(length<1e-12) throw new Error('tubeTriangles: a path sample has no direction')
    if(Math.hypot(t[0],t[1])>=1e-9*length) { frames.push(frameFor(t)); continue }
    const lateral=frames.at(-1)?.lateral ?? (horizontal ? frameFor(horizontal).lateral : [1,0,0] as Vec3)
    const up: Vec3=[-t[2]*lateral[1]/length,t[2]*lateral[0]/length,0]
    frames.push({lateral,up})
  }

  const at = (i: number, angle: number): Vec3 => {
    const p = points[i]!
    const f = frames[i]!
    const c = Math.cos(angle)
    const s = Math.sin(angle)
    return [
      p[0] + (f.lateral[0] * c + f.up[0] * s) * radius,
      p[1] + (f.lateral[1] * c + f.up[1] * s) * radius,
      p[2] + (f.lateral[2] * c + f.up[2] * s) * radius,
    ]
  }

  const out = new Float32Array((n - 1) * sides * 18)
  let w = 0
  const push = (p: Vec3): void => {
    out[w++] = p[0]
    out[w++] = p[1]
    out[w++] = p[2]
  }
  for (let i = 0; i < n - 1; i++) {
    for (let s = 0; s < sides; s++) {
      const a0 = (2 * Math.PI * s) / sides
      const a1 = (2 * Math.PI * (s + 1)) / sides
      const p00 = at(i, a0)
      const p01 = at(i, a1)
      const p10 = at(i + 1, a0)
      const p11 = at(i + 1, a1)
      push(p00)
      push(p10)
      push(p11)
      push(p00)
      push(p11)
      push(p01)
    }
  }
  return out
}
